import { useState } from 'react';
import { Trash2, X } from 'lucide-react';

import { pluralize } from '@/portainer/helpers/strings';

import { Button } from '@@/buttons';
import { Modal } from '@@/modals';
import { ModalHeader } from '@@/modals/Modal/ModalHeader';
import { ModalBody } from '@@/modals/Modal/ModalBody';
import { ModalFooter } from '@@/modals/Modal/ModalFooter';
import { LoadingButton } from '@@/buttons/LoadingButton';

interface DeleteTagsModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedTags: string[];
  onDeleteTags: () => Promise<void>;
}

export function DeleteTagsModal({
  isOpen,
  onClose,
  selectedTags,
  onDeleteTags,
}: DeleteTagsModalProps) {
  const [isLoading, setIsLoading] = useState(false);

  async function handleDelete() {
    setIsLoading(true);
    try {
      await onDeleteTags();
      // Only close after successful deletion
      setIsLoading(false);
      onClose();
    } catch {
      // Error handling is done by the parent component
      setIsLoading(false);
    }
  }

  function handleClose() {
    if (!isLoading) {
      onClose();
    }
  }

  if (!isOpen) {
    return null;
  }

  const tagCount = selectedTags.length;

  function getDeleteMessage() {
    const firstTag = <strong>{selectedTags[0]}</strong>;

    if (tagCount === 1) {
      return <>Are you sure you want to delete {firstTag}?</>;
    }

    const remaining =
      tagCount === 2 ? (
        <strong>{selectedTags[1]}</strong>
      ) : (
        <>
          {tagCount - 1} other {pluralize(tagCount - 1, 'tag')}
        </>
      );

    return (
      <>
        Are you sure you want to delete {firstTag} and {remaining}?
      </>
    );
  }

  return (
    <Modal onDismiss={handleClose} aria-label="Delete tags">
      <ModalHeader title="Delete Tags" />
      <ModalBody>
        <div>{getDeleteMessage()} This action cannot be undone.</div>
      </ModalBody>
      <ModalFooter>
        <Button
          type="button"
          color="default"
          onClick={handleClose}
          disabled={isLoading}
          icon={X}
          data-cy="delete-tags-cancel"
        >
          Cancel
        </Button>
        <LoadingButton
          type="button"
          color="danger"
          onClick={handleDelete}
          disabled={isLoading}
          isLoading={isLoading}
          loadingText="Removing..."
          icon={Trash2}
          data-cy="delete-tags-confirm"
        >
          Delete
        </LoadingButton>
      </ModalFooter>
    </Modal>
  );
}
